import {
    Button,
    Grid,
    IconButton,
    Paper,
    TableBody,
    TableContainer,
    TableRow,
    TextField,
} from "@mui/material";

import { zodResolver } from "@hookform/resolvers/zod";
import CancelIcon from "@mui/icons-material/Cancel";
import i18next from "i18next";
import { Controller, FormProvider, useForm, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { getCalculatedPricingForProduct } from "../../../utils";
import BasicDialog from "../../base/BasicDialog";
import { PriceDisplay } from "../../common/PriceDisplay";
import { StyledTableCell, StyledTableHead } from "../../table/BaseTable/BaseTable";
import { DiscountPercentInput } from "./DiscountPercentInput";
import { DiscountTotalInput } from "./DiscountTotalInput";

export const IncreaseDiscountRequestDialog = (props) => {
    const { initialProducts } = props;
    const { t } = useTranslation();

    const form = useForm({
        resolver: zodResolver(increaseDiscountRequestFormSchema),
        defaultValues: {
            comment: "",
            discountPercentages: initialProducts.reduce((acc, product) => {
                acc[product.id] = 0;
                return acc;
            }, {}),
        },
    });

    const onSubmit = (data) => {
        // TODO API call when implemented
        console.debug("IncreaseDiscountRequestDialog form submitted with data:", data);
        props.onClose();
    };

    const discountPercentages = form.watch("discountPercentages");
    const products = initialProducts.filter((p) => p.id in discountPercentages);

    const table = () => (
        <TableContainer component={Paper}>
            <StyledTableHead>
                <TableRow>
                    <StyledTableCell />
                    <StyledTableCell>
                        {t("IncreaseDiscountRequestDialog.table.number")}
                    </StyledTableCell>
                    <StyledTableCell>
                        {t("IncreaseDiscountRequestDialog.table.name")}
                    </StyledTableCell>
                    <StyledTableCell>
                        {t("IncreaseDiscountRequestDialog.table.productType")}
                    </StyledTableCell>
                    <StyledTableCell>
                        {t("IncreaseDiscountRequestDialog.table.maxDiscount")}
                    </StyledTableCell>
                    <StyledTableCell>
                        {t("IncreaseDiscountRequestDialog.table.requestedDiscountPercentage")}
                    </StyledTableCell>
                    <StyledTableCell>
                        {t("IncreaseDiscountRequestDialog.table.requestedDiscountAmount")}
                    </StyledTableCell>
                    <StyledTableCell>
                        {t("IncreaseDiscountRequestDialog.table.discountAmountPerSqm")}
                    </StyledTableCell>
                    <StyledTableCell>
                        {t("IncreaseDiscountRequestDialog.table.discountedPrice")}
                    </StyledTableCell>
                </TableRow>
            </StyledTableHead>
            <TableBody>
                {products.map((p) => (
                    <ProductTableRow key={p.id} product={p} handleClose={props.onClose} />
                ))}
            </TableBody>
        </TableContainer>
    );

    const commentInput = () => (
        <Controller
            name="comment"
            control={form.control}
            render={({ field, fieldState }) => (
                <TextField
                    {...field}
                    label={t("IncreaseDiscountRequestDialog.comment")}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                />
            )}
        />
    );

    return (
        <BasicDialog
            open={props.open}
            onClose={props.onClose}
            titleAlign={"center"}
            contentAlign={"center"}
            title={t("IncreaseDiscountRequestDialog.title")}
            minWidth={800}
            maxWidth={"xl"}
            showDialogActions
        >
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <Grid container direction="column">
                        <Grid item>{table()}</Grid>
                        <Grid item container direction="row">
                            <Grid item>{commentInput()}</Grid>
                            <Grid item>
                                <Button type={"submit"}>
                                    {t("IncreaseDiscountRequestDialog.submit")}
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </form>
            </FormProvider>
        </BasicDialog>
    );
};

const ProductTableRow = (props) => {
    const { product, handleClose } = props;
    const discountsFormFieldName = "discountPercentages";
    const productFormFieldName = `${discountsFormFieldName}.${product.id}`;

    const pricing = getCalculatedPricingForProduct(product);
    const basePrice = pricing.listed.gross;

    const { setValue } = useFormContext();
    const allDiscounts = useWatch({ name: discountsFormFieldName }) || {};
    const discountPercent = useWatch({ name: productFormFieldName });

    const discountPerSqm =
        product.footage != null ? (basePrice * (discountPercent / 100)) / product.footage : 0;
    const discountedPrice = basePrice - (basePrice * discountPercent) / 100;

    const deleteProduct = (productId) => {
        const updatedDiscounts = { ...allDiscounts };
        delete updatedDiscounts[productId];
        setValue(discountsFormFieldName, updatedDiscounts);

        if (Object.keys(updatedDiscounts).length === 0) {
            handleClose();
        }
    };

    return (
        <TableRow>
            <StyledTableCell>
                <IconButton onClick={() => deleteProduct(product.id)}>
                    <CancelIcon color={"error"} />
                </IconButton>
            </StyledTableCell>
            <StyledTableCell>{product.product_number}</StyledTableCell>
            <StyledTableCell>{product.name_full}</StyledTableCell>
            <StyledTableCell>{product.type?.name}</StyledTableCell>
            <StyledTableCell>{product.pricing.discount_max}</StyledTableCell>
            <StyledTableCell>
                <DiscountPercentInput
                    fieldName={productFormFieldName}
                    range={{ min: 0, max: 100 }}
                />
            </StyledTableCell>
            <StyledTableCell>
                <DiscountTotalInput
                    fieldName={productFormFieldName}
                    range={{ min: 0, max: basePrice }}
                    productPrice={basePrice}
                />
            </StyledTableCell>
            <StyledTableCell>
                <PriceDisplay price={discountPerSqm} />
            </StyledTableCell>
            <StyledTableCell>
                <PriceDisplay price={discountedPrice} />
            </StyledTableCell>
        </TableRow>
    );
};

const increaseDiscountRequestFormSchema = z.object({
    comment: z
        .string()
        .min(
            1,
            i18next.t("IncreaseDiscountRequestDialog.commentRequired") ?? "Comment cannot be empty",
        ),
    discountPercentages: z.record(z.string().uuid(), z.number().min(0).max(100)),
});
